import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  type JELineFormValue,
  JournalEntryLineRow,
} from '../../components/bookkeeping/JournalEntryLineRow.js';
import type { Account, AccountNode } from '../../types/bookkeeping.js';
import type { ConfigStatus } from '../../types/onboarding.js';

const FX_SCALE = 1_000_000_000n;

function flattenTree(nodes: AccountNode[]): Account[] {
  const result: Account[] = [];
  function walk(ns: AccountNode[]) {
    for (const n of ns) {
      result.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

function defaultLine(): JELineFormValue {
  return { account_id: '', currency: 'USD', amount: '', fx_rate: FX_SCALE.toString() };
}

interface JELine {
  account_id: string;
  currency: string;
  amount: string;
  fx_rate: string;
  description?: string | null;
}

interface JEEntry {
  id: string;
  number: string | number;
  entry_date: string;
  memo: string | null;
}

export function JournalEntry() {
  const { t } = useTranslation('bookkeeping');
  const nav = useNavigate();
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  const { id: entryId } = useParams<{ id: string }>();
  const isNew = !entryId;

  const today = new Date().toISOString().slice(0, 10);
  const [entryDate, setEntryDate] = useState(today);
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<JELineFormValue[]>([defaultLine(), defaultLine()]);
  const [seeded, setSeeded] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch accounts (flat list from tree)
  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });
  const accounts = flattenTree(accountsData?.tree ?? []);

  // Fetch org config for base currency
  const { data: configData } = useQuery({
    queryKey: ['config-status', activeOrgId],
    queryFn: () => api<ConfigStatus>(`/orgs/${activeOrgId}/config-status`),
    enabled: !!activeOrgId,
  });
  const baseCurrency = configData?.organization.baseCurrency ?? 'USD';

  // Fetch existing entry if editing
  useQuery({
    queryKey: ['bk-je', entryId],
    queryFn: () =>
      api<{ entry: JEEntry; lines: JELine[] }>(`/bookkeeping/journal-entries/${entryId}`),
    enabled: !isNew && !!entryId && !seeded,
    onSuccess: (data: { entry: JEEntry; lines: JELine[] }) => {
      if (!seeded) {
        setEntryDate(data.entry.entry_date);
        setMemo(data.entry.memo ?? '');
        setLines(
          data.lines.map(
            (l): JELineFormValue => ({
              account_id: l.account_id,
              currency: l.currency,
              amount: l.amount,
              fx_rate: l.fx_rate,
              ...(l.description != null ? { description: l.description } : {}),
            }),
          ),
        );
        setSeeded(true);
      }
    },
  } as Parameters<typeof useQuery>[0]);

  // Live balance calculation
  const totalBase = lines.reduce((s, l) => {
    if (!l.amount || !l.fx_rate) return s;
    try {
      return s + (BigInt(l.amount) * BigInt(l.fx_rate)) / FX_SCALE;
    } catch {
      return s;
    }
  }, 0n);

  const isBalanced = totalBase === 0n;
  const isComplete =
    lines.length >= 2 && lines.every((l) => l.account_id && l.currency && l.amount && l.fx_rate);
  const canSubmit = isBalanced && isComplete;

  const submit = useMutation({
    mutationFn: () =>
      isNew
        ? api('/bookkeeping/journal-entries', {
            method: 'POST',
            body: JSON.stringify({
              entry_date: entryDate,
              memo: memo || null,
              lines: lines.map((l) => ({
                account_id: l.account_id,
                currency: l.currency,
                amount: l.amount,
                fx_rate: l.fx_rate,
                description: l.description || null,
              })),
            }),
          })
        : api(`/bookkeeping/journal-entries/${entryId}`, {
            method: 'PATCH',
            body: JSON.stringify({
              entry_date: entryDate,
              memo: memo || null,
              lines: lines.map((l) => ({
                account_id: l.account_id,
                currency: l.currency,
                amount: l.amount,
                fx_rate: l.fx_rate,
                description: l.description || null,
              })),
            }),
          }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bk-jes'] });
      if (!isNew) await qc.invalidateQueries({ queryKey: ['bk-je', entryId] });
      nav('/app/bookkeeping/journal-entries');
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setSubmitError(err.body?.error ?? err.message ?? 'Error desconocido');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {isNew ? t('journalEntries.newTitle') : t('journalEntries.editTitle')}
        </h1>
        <button
          type="button"
          onClick={() => nav('/app/bookkeeping/journal-entries')}
          className="rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          {t('journalEntries.cancel')}
        </button>
      </div>

      {/* Header */}
      <div className="flex gap-3">
        <div>
          <label className="block text-xs text-slate-500">
            {t('journalEntries.fields.date')}
            <input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              className="rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>
        <div className="flex-1">
          <label className="block text-xs text-slate-500">
            {t('journalEntries.fields.memo')}
            <input
              placeholder={t('journalEntries.fields.memoPlaceholder')}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>
      </div>

      {/* Lines table */}
      <div className="overflow-x-auto rounded border bg-white">
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-2">{t('journalEntries.columns.account')}</th>
              <th className="px-2 py-2">{t('journalEntries.columns.currency')}</th>
              <th className="px-2 py-2">{t('journalEntries.columns.amount')}</th>
              <th className="px-2 py-2">{t('journalEntries.columns.fxRate')}</th>
              <th className="px-2 py-2 text-right">
                {t('journalEntries.columns.baseAmount')} ({baseCurrency})
              </th>
              <th className="px-2 py-2">{t('journalEntries.columns.description')}</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <JournalEntryLineRow
                // biome-ignore lint/suspicious/noArrayIndexKey: journal lines are ordered form rows with no stable ID
                key={i}
                index={i}
                value={l}
                accounts={accounts}
                entryDate={entryDate}
                baseCurrency={baseCurrency}
                canRemove={lines.length > 2}
                onChange={(v) => {
                  setSubmitError(null);
                  setLines((ls) => ls.map((x, j) => (i === j ? v : x)));
                }}
                onRemove={() => setLines((ls) => ls.filter((_, j) => j !== i))}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Add line */}
      <button
        type="button"
        onClick={() => setLines((ls) => [...ls, defaultLine()])}
        className="rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
      >
        + {t('journalEntries.addLine')}
      </button>

      {/* Footer: balance + submit */}
      <div className="flex items-center justify-between rounded border bg-slate-50 px-4 py-3">
        <div className="text-sm">
          <span className="text-slate-500">{t('journalEntries.runningSum')}: </span>
          <strong className={isBalanced ? 'text-green-700' : 'text-red-600'}>
            {totalBase.toString()} {baseCurrency}
          </strong>
          {isBalanced ? (
            <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
              {t('journalEntries.balanced')}
            </span>
          ) : (
            <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-600">
              {t('journalEntries.unbalanced')}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => nav('/app/bookkeeping/journal-entries')}
            className="rounded border px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
          >
            {t('journalEntries.cancel')}
          </button>
          <button
            type="button"
            disabled={!canSubmit || submit.isPending}
            onClick={() => {
              setSubmitError(null);
              submit.mutate();
            }}
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {submit.isPending ? '…' : t('journalEntries.submit')}
          </button>
        </div>
      </div>

      {submitError && <p className="text-sm text-red-600">{submitError}</p>}
    </div>
  );
}
